import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const VideoPage = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/videos`);
      setVideos(response.data.data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Gagal memuat video');
    } finally {
      setLoading(false);
    }
  };

  const openVideo = (video) => {
    setSelectedVideo(video);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat video...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-ltb-r text-white p-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="mb-2 flex items-center text-white/80 hover:text-white"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali
          </button>
          <h1 className="text-2xl font-bold">Video</h1>
          <p className="text-white/80 text-sm mt-1">Live streaming dan rekaman turnamen</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {videos.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="text-6xl mb-4">🎬</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Belum Ada Video</h3>
            <p className="text-gray-500">Video akan ditampilkan di sini saat tersedia</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Live Videos First */}
            {videos.filter(v => v.is_live).length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-red-500 rounded-full mr-2 animate-pulse"></span>
                  Live Streaming
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {videos.filter(v => v.is_live).map((video) => (
                    <div 
                      key={video.id} 
                      className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => openVideo(video)}
                    >
                      <div className="relative">
                        <img 
                          src={video.thumbnail}
                          alt={video.caption}
                          className="w-full h-48 object-cover"
                          onError={(e) => { 
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" fill="%23374151"><rect width="320" height="180"/><text x="50%" y="50%" text-anchor="middle" fill="%239CA3AF" font-size="14">Video</text></svg>';
                          }}
                        />
                        <span className="absolute top-3 left-3 bg-red-600 text-white text-xs px-3 py-1 rounded-full flex items-center space-x-1 shadow-lg">
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                          <span className="font-semibold">LIVE</span>
                        </span>
                        {/* Play button */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                            <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-800 line-clamp-2">{video.caption}</h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Regular Videos */}
            {videos.filter(v => !v.is_live).length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="mr-2">🎬</span>
                  Video Rekaman
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {videos.filter(v => !v.is_live).map((video) => (
                    <div 
                      key={video.id} 
                      className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => openVideo(video)}
                    >
                      <div className="relative">
                        <img 
                          src={video.thumbnail}
                          alt={video.caption}
                          className="w-full h-40 object-cover"
                          onError={(e) => { 
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" fill="%23374151"><rect width="320" height="180"/><text x="50%" y="50%" text-anchor="middle" fill="%239CA3AF" font-size="14">Video</text></svg>';
                          }}
                        />
                        {/* Play button */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                          <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                            <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium text-gray-800 line-clamp-2">{video.caption}</h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeVideo}
        >
          <div className="w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            {/* Close button */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                {selectedVideo.is_live && (
                  <span className="bg-red-600 text-white text-xs px-3 py-1 rounded-full flex items-center space-x-1 mr-3">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    <span className="font-semibold">LIVE</span>
                  </span>
                )}
                <h3 className="text-white font-semibold text-lg line-clamp-1">{selectedVideo.caption}</h3>
              </div>
              <button 
                onClick={closeVideo}
                className="text-white/80 hover:text-white p-2"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* YouTube Embed */}
            <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-xl bg-black">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1&rel=0`}
                title={selectedVideo.caption}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            {/* Caption */}
            <p className="text-white/80 mt-4 text-center">{selectedVideo.caption}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPage;
